// apps/web/src/components/coletor/relogios-independentes.tsx — Fase 02, Plano 06 (APT-04, APT-05, D-12).
import { useState, useEffect, type ReactElement } from 'react';
import { enfileirarEvento } from '../../lib/offline/outbox';
import { apiMutate } from '../../lib/csrf-fetch';

export type RelogioTipo = 'MAQUINA' | 'MAO_DE_OBRA';
export type FaseTipo = 'PREPARACAO' | 'PROCESSO' | 'ESPERA' | 'MOVIMENTACAO';

function mensagemDeErro(erro: unknown): string | null {
  return erro instanceof Error && erro.message !== 'Failed to fetch' ? erro.message : null;
}

interface RelogioState {
  readonly rodando: boolean;
  readonly segundos: number;
  readonly fase: FaseTipo;
  readonly sessaoId: string | null;
}

interface RelogiosIndependentesProps {
  readonly osId: string;
}

const MOTIVOS_PARADA = [
  'Manutenção Mecânica / Elétrica',
  'Ajuste de Parâmetros Químicos (Banho)',
  'Troca de Ferramental / Gancheiras',
  'Falta de Insumo / Peças',
  'Intervalo / Almoço',
  'Outros',
];

export function RelogiosIndependentes({ osId }: RelogiosIndependentesProps): ReactElement {
  const [maquina, setMaquina] = useState<RelogioState>({
    rodando: false,
    segundos: 0,
    fase: 'PREPARACAO',
    sessaoId: null,
  });

  const [maoDeObra, setMaoDeObra] = useState<RelogioState>({
    rodando: false,
    segundos: 0,
    fase: 'PROCESSO',
    sessaoId: null,
  });

  // Modal de pausa
  const [modalPausa, setModalPausa] = useState<{
    open: boolean;
    relogio: RelogioTipo;
    sessaoId: string | null;
  }>({ open: false, relogio: 'MAQUINA', sessaoId: null });

  const [motivoSelecionado, setMotivoSelecionado] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  // Timer tick
  useEffect(() => {
    const interval = setInterval(() => {
      if (maquina.rodando) {
        setMaquina((prev) => ({ ...prev, segundos: prev.segundos + 1 }));
      }
      if (maoDeObra.rodando) {
        setMaoDeObra((prev) => ({ ...prev, segundos: prev.segundos + 1 }));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [maquina.rodando, maoDeObra.rodando]);

  const formatTempo = (segundosTotais: number): string => {
    const h = Math.floor(segundosTotais / 3600);
    const m = Math.floor((segundosTotais % 3600) / 60);
    const s = segundosTotais % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const handleIniciar = async (relogio: RelogioTipo, fase: FaseTipo) => {
    setLoading(true);
    let sessaoId: string = crypto.randomUUID();
    try {
      const res = await apiMutate('/api/v1/apontamentos/sessao/iniciar', {
        method: 'POST',
        body: { osId, relogio, fase },
      });
      if (!res.ok) {
        const errJson = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(errJson.message || 'Falha ao iniciar sessão');
      }
      const data = (await res.json()) as { id: string };
      sessaoId = data.id;
      
      if (relogio === 'MAQUINA') {
        setMaquina((prev) => ({ ...prev, rodando: true, fase, sessaoId }));
      } else {
        setMaoDeObra((prev) => ({ ...prev, rodando: true, fase, sessaoId }));
      }
    } catch (erro) {
      const mensagem = mensagemDeErro(erro);
      if (mensagem) {
        alert(mensagem);
      } else {
        // Fallback local via outbox
        await enfileirarEvento({
          idempotencyId: crypto.randomUUID(),
          aggregateType: 'ApontamentoSessao',
          aggregateId: sessaoId,
          deviceId: 'coletor-local',
          schemaVersion: 1,
          baseVersion: 0,
          payload: { action: 'INICIAR', osId, relogio, fase, id: sessaoId },
          clientTime: new Date().toISOString(),
        });
        if (relogio === 'MAQUINA') setMaquina((prev) => ({ ...prev, rodando: true, fase, sessaoId }));
        else setMaoDeObra((prev) => ({ ...prev, rodando: true, fase, sessaoId }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAbrirPausa = (relogio: RelogioTipo) => {
    const sessaoId = relogio === 'MAQUINA' ? maquina.sessaoId : maoDeObra.sessaoId;
    setMotivoSelecionado('');
    setModalPausa({ open: true, relogio, sessaoId });
  };

  const handleConfirmarPausa = async () => {
    if (!motivoSelecionado) return;
    setLoading(true);
    const { sessaoId, relogio } = modalPausa;
    try {
      if (sessaoId) {
        const res = await apiMutate('/api/v1/apontamentos/sessao/pausar', {
          method: 'POST',
          body: { sessaoId, motivoParada: motivoSelecionado },
        });
        if (!res.ok) {
          const errJson = (await res.json().catch(() => ({}))) as { message?: string };
          throw new Error(errJson.message || 'Falha ao pausar sessão');
        }
      }
      
      if (relogio === 'MAQUINA') {
        setMaquina((prev) => ({ ...prev, rodando: false, sessaoId: null }));
      } else {
        setMaoDeObra((prev) => ({ ...prev, rodando: false, sessaoId: null }));
      }
      setModalPausa({ open: false, relogio: 'MAQUINA', sessaoId: null });
    } catch (erro) {
      const mensagem = mensagemDeErro(erro);
      if (mensagem) {
        alert(mensagem);
      } else if (sessaoId) {
        // Fallback local via outbox
        await enfileirarEvento({
          idempotencyId: crypto.randomUUID(),
          aggregateType: 'ApontamentoSessao',
          aggregateId: sessaoId,
          deviceId: 'coletor-local',
          schemaVersion: 1,
          baseVersion: 0,
          payload: { action: 'PAUSAR', sessaoId, motivoParada: motivoSelecionado },
          clientTime: new Date().toISOString(),
        });
        if (relogio === 'MAQUINA') setMaquina((prev) => ({ ...prev, rodando: false, sessaoId: null }));
        else setMaoDeObra((prev) => ({ ...prev, rodando: false, sessaoId: null }));
        setModalPausa({ open: false, relogio: 'MAQUINA', sessaoId: null });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relogios-container grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Relógio 1: Máquina */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 flex flex-col justify-between shadow-md">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Relógio Máquina</span>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-slate-700 text-slate-300">
              Fase: {maquina.fase}
            </span>
          </div>
          <div className="text-3xl sm:text-4xl font-mono font-bold text-emerald-300 py-3 tracking-widest text-center bg-slate-900/60 rounded-lg border border-slate-750 my-2">
            {formatTempo(maquina.segundos)}
          </div>
        </div>

        <div className="pt-3">
          {maquina.rodando ? (
            <button
              type="button"
              onClick={() => handleAbrirPausa('MAQUINA')}
              className="w-full min-h-[56px] py-3 bg-amber-600 hover:bg-amber-500 text-white font-bold text-sm rounded-lg active:bg-amber-700 transition touch-manipulation"
            >
              Pausar Máquina
            </button>
          ) : (
            <button
              type="button"
              disabled={loading}
              onClick={() => void handleIniciar('MAQUINA', 'PROCESSO')}
              className="w-full min-h-[56px] py-3 bg-emerald-700 hover:bg-emerald-600 text-white font-bold text-sm rounded-lg active:bg-emerald-800 transition touch-manipulation"
            >
              Iniciar Máquina
            </button>
          )}
        </div>
      </div>

      {/* Relógio 2: Mão de Obra */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 flex flex-col justify-between shadow-md">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Relógio Mão de Obra</span>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-slate-700 text-slate-300">
              Fase: {maoDeObra.fase}
            </span>
          </div>
          <div className="text-3xl sm:text-4xl font-mono font-bold text-emerald-400 py-3 tracking-widest text-center bg-slate-900/60 rounded-lg border border-slate-750 my-2">
            {formatTempo(maoDeObra.segundos)}
          </div>
        </div>

        <div className="pt-3">
          {maoDeObra.rodando ? (
            <button
              type="button"
              onClick={() => handleAbrirPausa('MAO_DE_OBRA')}
              className="w-full min-h-[56px] py-3 bg-amber-600 hover:bg-amber-500 text-white font-bold text-sm rounded-lg active:bg-amber-700 transition touch-manipulation"
            >
              Pausar Mão de Obra
            </button>
          ) : (
            <button
              type="button"
              disabled={loading}
              onClick={() => void handleIniciar('MAO_DE_OBRA', 'PROCESSO')}
              className="w-full min-h-[56px] py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm rounded-lg active:bg-emerald-700 transition touch-manipulation"
            >
              Iniciar Mão de Obra
            </button>
          )}
        </div>
      </div>

      {/* Modal de Pausa com Motivo Categorizado (D-12) */}
      {modalPausa.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-lg w-full p-6 space-y-4 shadow-2xl">
            <h3 className="text-lg font-bold text-white">
              Pausar relógio: {modalPausa.relogio === 'MAQUINA' ? 'Máquina' : 'Mão de Obra'}
            </h3>
            <p className="text-xs text-slate-400">
              Selecione obrigatoriamente o motivo da parada para suspender a contagem.
            </p>

            <div className="space-y-2 py-2">
              {MOTIVOS_PARADA.map((motivo) => (
                <label
                  key={motivo}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer min-h-[52px] transition ${
                    motivoSelecionado === motivo
                      ? 'bg-blue-950/50 border-emerald-500 text-white'
                      : 'bg-slate-800/60 border-slate-700 text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  <input
                    type="radio"
                    name="motivoParada"
                    value={motivo}
                    checked={motivoSelecionado === motivo}
                    onChange={() => setMotivoSelecionado(motivo)}
                    className="w-5 h-5 text-blue-600 focus:ring-emerald-400"
                  />
                  <span className="text-sm font-medium">{motivo}</span>
                </label>
              ))}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setModalPausa({ open: false, relogio: 'MAQUINA', sessaoId: null })}
                className="px-5 py-3 border border-slate-700 text-slate-300 text-sm font-medium rounded-lg hover:bg-slate-800"
              >
                Voltar
              </button>
              <button
                type="button"
                disabled={!motivoSelecionado || loading}
                onClick={() => void handleConfirmarPausa()}
                className="px-6 py-3 bg-amber-600 hover:bg-amber-500 text-white text-sm font-bold rounded-lg disabled:opacity-40"
              >
                {loading ? 'Pausando...' : 'Confirmar Pausa'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
